import { CheckCircle2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, type Operator, session } from '../api/client';
import { useInvitationDetails } from '../api/hooks';
import { LanguageSwitch } from '../components/language-switch';
import { Button, ErrorNote, Field, Input, Loading } from '../components/ui';
import { useApiError } from '../i18n/useApiError';

export function AcceptInvitePage({ onAccepted }: { onAccepted: (operator: Operator) => void }) {
  const { t } = useTranslation();
  const describeError = useApiError();
  const token = new URLSearchParams(window.location.hash.slice(1)).get('token') ?? '';
  const tokenLooksValid = token.length >= 32;
  const invitation = useInvitationDetails(token);
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    if (password.length < 10) return setError(t('acceptInvite.passwordTooShort'));
    if (password !== confirmation) return setError(t('acceptInvite.passwordMismatch'));
    setBusy(true);
    try {
      const result = await api.post<{
        accessToken: string;
        refreshToken: string;
        operator: Operator;
      }>('/admin/auth/invitations/accept', { token, password });
      session.save(result);
      onAccepted(result.operator);
    } catch (cause) {
      setError(describeError(cause, 'acceptInvite.failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-full flex-col bg-surface-shell">
      <header className="flex justify-end p-4">
        <LanguageSwitch />
      </header>
      <main className="flex flex-1 items-center justify-center p-6">
        <div className="w-full max-w-md">
          {tokenLooksValid && invitation.isPending ? <Loading /> : null}
          {invitation.isError || !tokenLooksValid ? (
            <div className="rounded-lg border border-hairline bg-white p-6">
              <h1 className="text-xl font-bold">{t('acceptInvite.invalidTitle')}</h1>
              <p className="mt-2 text-sm text-muted">{t('acceptInvite.invalidBody')}</p>
            </div>
          ) : invitation.data ? (
            <form
              onSubmit={submit}
              className="flex flex-col gap-5 rounded-lg border border-hairline bg-white p-6"
            >
              <div className="flex flex-col gap-2">
                <CheckCircle2 size={26} />
                <h1 className="text-xl font-bold">{t('acceptInvite.title')}</h1>
                <p className="text-sm text-muted">
                  {t('acceptInvite.subtitle', {
                    name: invitation.data.name,
                    organization: invitation.data.organizationName,
                  })}
                </p>
                <p className="text-xs text-faint">{invitation.data.email}</p>
              </div>
              <div className="border-y border-hairline py-3">
                <p className="mb-2 text-xs font-semibold">{t('team.lotScope')}</p>
                <div className="grid gap-1.5 sm:grid-cols-2">
                  {invitation.data.lots.map((lot) => (
                    <div key={lot.id} className="flex min-w-0 flex-col">
                      <span className="truncate text-xs font-semibold">{lot.name}</span>
                      <span className="font-mono text-[11px] text-muted">{lot.code}</span>
                    </div>
                  ))}
                </div>
              </div>
              <Field label={t('acceptInvite.password')} hint={t('acceptInvite.passwordHint')}>
                <Input
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  required
                />
              </Field>
              <Field label={t('acceptInvite.confirmPassword')}>
                <Input
                  type="password"
                  autoComplete="new-password"
                  value={confirmation}
                  onChange={(event) => setConfirmation(event.target.value)}
                  required
                />
              </Field>
              {error ? <ErrorNote message={error} /> : null}
              <Button type="submit" variant="dark" disabled={busy}>
                {t(busy ? 'acceptInvite.accepting' : 'acceptInvite.accept')}
              </Button>
            </form>
          ) : null}
        </div>
      </main>
    </div>
  );
}
